import { Trans, useTranslation } from "react-i18next";

import { removeLeadingZeros, removeNonDigitCharacters } from "shared/utils";

import TextField from "features/components/TextField";
import DropdownSelect, {
  DropdownSelectOption,
} from "features/ui/DropdownSelect/DropdownSelect";
import {
  MAX_WINDOW_SIZE,
  MIN_WINDOW_SIZE,
} from "features/ui/Filters/FilterTypes/OccursFilter/constants";
import { getSelectOptionValue } from "features/ui/Filters/FilterTypes/OccursFilter/utils";
import {
  OccursFilterWindowDirection,
  OccursFilterWindowDirectionType,
  OccursWindowOptions,
} from "features/ui/Filters/types";
import {
  isDateOrDateTimeField,
  onlyAllowPositiveIntegersOnKeyDown,
} from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

interface Props {
  occursOptionsState: OccursWindowOptions;
  onUpdate: (row: OccursWindowOptions) => void;
  baseEntityText: string;
  windowDirections?: OccursFilterWindowDirectionType[];
  disabled?: boolean;
  disableSelectingWindowDirection?: boolean;
  schema?: SchemaEntry[];
  fieldName?: string;
  showThatOccurPrefix?: boolean;
}

const ALL_WINDOW_DIRECTIONS: OccursFilterWindowDirectionType[] = [
  OccursFilterWindowDirection.BEFORE,
  OccursFilterWindowDirection.AFTER,
  OccursFilterWindowDirection.BOTH,
];

const WINDOW_DIRECTION_TEST_IDS: Record<
  OccursFilterWindowDirectionType,
  string
> = {
  [OccursFilterWindowDirection.BEFORE]: "before",
  [OccursFilterWindowDirection.AFTER]: "after",
  [OccursFilterWindowDirection.BOTH]: "before-or-after",
};

const OccursTimeWindowForm = ({
  occursOptionsState,
  onUpdate,
  baseEntityText,
  windowDirections = ALL_WINDOW_DIRECTIONS,
  disableSelectingWindowDirection = false,
  disabled,
  schema,
  fieldName,
  showThatOccurPrefix = false,
}: Props) => {
  const { t } = useTranslation();

  const windowDirectionOptions: DropdownSelectOption<OccursFilterWindowDirectionType>[] =
    windowDirections.map((direction) => ({
      id: direction,
      value: t(`filters:occurs.direction.${direction}`),
      label: t(`filters:occurs.direction.${direction}`),
      testId: WINDOW_DIRECTION_TEST_IDS[direction],
    }));

  // get date fields from schema
  const dateFieldOptions = [
    ...(schema
      ?.filter((entry) => isDateOrDateTimeField(entry.dataType))
      .map((entry) => ({
        id: entry.accessor,
        value: entry.label,
        label: entry.label,
        testId: `date-field-${entry.accessor}`,
      })) || []),
  ];

  // find the selected date field option or use the default
  const getDefaultDateField = () => {
    if (fieldName === "relatedSignalEventOccurrences") {
      return (
        dateFieldOptions.find((option) => option.id === "recordedAt") ||
        dateFieldOptions[0]
      );
    }

    return (
      dateFieldOptions.find((option) => option.id === "date") ||
      dateFieldOptions[0]
    );
  };

  const selectedDateField = occursOptionsState.dateField
    ? dateFieldOptions.find(
        (option) => option.id === occursOptionsState.dateField
      )
    : getDefaultDateField();

  const sizeInput = (
    <TextField
      className="w-20! min-w-[5rem]"
      fullWidth={false}
      type="number"
      value={occursOptionsState.windowSize}
      testId="related-events-window-size"
      slotProps={{
        htmlInput: { min: MIN_WINDOW_SIZE, max: MAX_WINDOW_SIZE },
      }}
      onKeyDown={onlyAllowPositiveIntegersOnKeyDown}
      disabled={disabled}
      sx={{
        "& .MuiInputBase-input": { paddingTop: "4px", paddingBottom: "4px" },
      }}
      onChange={(e) => {
        // Only handle upper bound, lower bound is handled in onBlur
        let value = removeNonDigitCharacters(e.target.value);
        value = removeLeadingZeros(value);
        if (parseInt(value) > MAX_WINDOW_SIZE) {
          value = MAX_WINDOW_SIZE.toString();
        }

        onUpdate({
          ...occursOptionsState,
          windowSize: value,
        });
      }}
      onBlur={(e) => {
        // if the input is empty or zero, reset to the minimum window size
        if (e.target.value === "" || e.target.value === "0") {
          onUpdate({
            ...occursOptionsState,
            windowSize: MIN_WINDOW_SIZE,
          });
        }
      }}
    />
  );

  const directionControl =
    windowDirectionOptions?.length === 1 ? (
      <div>{windowDirectionOptions[0].value}</div>
    ) : (
      <DropdownSelect
        options={windowDirectionOptions}
        onSelect={(selectedOption) => {
          onUpdate({
            ...occursOptionsState,
            windowDirection: selectedOption.id,
          });
        }}
        label={getSelectOptionValue(
          occursOptionsState.windowDirection,
          windowDirectionOptions
        )}
        testId="related-events-window-direction"
        size="small"
        disabled={disabled}
      />
    );

  const dateFieldControl =
    dateFieldOptions.length > 0 ? (
      <DropdownSelect
        options={dateFieldOptions}
        onSelect={(selectedOption) => {
          onUpdate({
            ...occursOptionsState,
            dateField: selectedOption.id,
          });
        }}
        label={selectedDateField?.label}
        testId="related-events-date-field"
        size="small"
        disabled={disabled}
      />
    ) : (
      <></>
    );

  const sentenceKey = `filters:occurs.form.${showThatOccurPrefix ? "that_occur_" : ""}${
    disableSelectingWindowDirection ? "before_now" : "relative"
  }`;

  return (
    <div
      className="flex items-center gap-2 text-sm"
      data-testid="occurs-time-window-form"
    >
      <Trans
        t={t}
        i18nKey={sentenceKey}
        count={Number(occursOptionsState.windowSize)}
        values={{ entity: baseEntityText }}
        components={{
          size: sizeInput,
          direction: directionControl,
          entity: <div className="whitespace-nowrap" />,
          dateField: dateFieldControl,
        }}
      />
    </div>
  );
};

export default OccursTimeWindowForm;
